import { useQueryClient } from "@tanstack/react-query";
import type { Editor, EditorOptions } from "@tiptap/react";
import { useEditor } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { toast } from "sonner";

import {
  getGetEncounterByIdQueryKey,
  useGetEncounterById,
  useUpdateEncounterById,
} from "@elation/api-client/src/stitch-web";

import { useActor } from "@src/shared/providers/actorProvider";
import { getServerErrorMessage } from "@src/shared/utils/http/errors";
import { captureException } from "@src/shared/utils/observability/sentry";

import {
  EMPTY_DOC,
  parseScratchpadToEditorContent,
  serializeScratchpadDoc,
} from "./scratchpad-format";
import {
  createScratchpadSaveQueue,
  createScratchpadSaveState,
  type ScratchpadSaveQueue,
} from "./scratchpad-save-queue";

export type AgendaScratchpadStatus = "idle" | "saving" | "error";

export interface UseAgendaScratchpadOptions {
  encounterId?: number;
  disabled?: boolean;
}

export interface UseAgendaScratchpadResult {
  /** False when there is no encounter to bind the scratch pad to. */
  canLoad: boolean;
  editor: Editor | null;
  isEmpty: boolean;
  isError: boolean;
  isFetching: boolean;
  isSuccess: boolean;
  status: AgendaScratchpadStatus;
  onRetry: () => void;
}

const PLACEHOLDER_TEXT = "Jot prep notes for this visit…";
const AUTOSAVE_MS = 400;
/** Stable id so repeated save failures collapse into one toast, not a stack. */
const SCRATCHPAD_SAVE_ERROR_TOAST_ID = "agenda-scratchpad-save-error";
const scratchpadSaveState = createScratchpadSaveState();

const EDITOR_EXTENSIONS = [StarterKit];

// `h-full` fills a definite-height flex ancestor (the AgendaDock's expanded
// scratch-pad section, whose height is set inline); standalone usage has no
// such ancestor, so it resolves to `auto` and `min-h-24` is the effective
// floor there, matching pre-dock sizing. No resize handle: a browser resize
// grip inside a fixed-height section is a layout bug (it can grow past the
// section's bounds), so `resize-y` is gone.
const EDITOR_PROPS: NonNullable<EditorOptions["editorProps"]> = {
  attributes: {
    role: "textbox",
    "aria-multiline": "true",
    "aria-label": "Scratch pad notes",
    class:
      "min-h-24 h-full overflow-y-auto px-3 py-2 text-[13px] leading-relaxed text-foreground outline-none",
  },
  handlePaste(view, event) {
    const text = event.clipboardData?.getData("text/plain");
    if (text == null) return false;
    event.preventDefault();
    view.pasteText(text);
    return true;
  },
};

function noop() {
  return undefined;
}

/**
 * Owns the scratch pad's TipTap editor instance and autosave lifecycle,
 * independent of whatever chrome renders around it. Callers that toggle
 * visibility (e.g. `AgendaDock`'s expand/collapse toggle) should hold this
 * hook at a level that stays mounted across the toggle, so the editor
 * instance and its in-flight/pending saves survive being shown and hidden.
 * Only the presentational shell (`AgendaScratchPadView`) needs to
 * mount/unmount.
 */
export function useAgendaScratchpad({
  encounterId,
  disabled = false,
}: UseAgendaScratchpadOptions): UseAgendaScratchpadResult {
  const actor = useActor();
  const queryClient = useQueryClient();
  const saveScope = JSON.stringify([
    actor.type,
    actor.user.id,
    actor.practice.id,
  ]);
  const canLoad = encounterId != null;
  const encounterQuery = useGetEncounterById(encounterId ?? 0, {
    query: { enabled: canLoad, retry: false },
  });
  const { mutateAsync: updateEncounter } = useUpdateEncounterById();
  const [isEmpty, setIsEmpty] = useState(true);
  const [pendingSaveCount, setPendingSaveCount] = useState(0);

  const dirtyRef = useRef(false);
  const notesRef = useRef("");
  const mountedRef = useRef(true);
  const boundEncounterIdRef = useRef(encounterId);
  const boundSaveScopeRef = useRef(saveScope);
  const disabledRef = useRef(disabled);
  const updateEncounterRef = useRef(updateEncounter);
  const queryClientRef = useRef(queryClient);
  const saveTimerRef = useRef<number | null>(null);
  const scheduleSaveRef = useRef(noop);
  const clearSaveTimerRef = useRef(noop);
  const enqueueSaveRef = useRef(
    (_id: number, _notes: string, _bestEffort = false) => undefined,
  );
  const flushBoundEncounterRef = useRef(
    (_options?: { bestEffort?: boolean }) => undefined,
  );
  const saveQueueRef = useRef<ScratchpadSaveQueue | null>(null);
  const saveQueueScopeRef = useRef<string | null>(null);

  disabledRef.current = disabled;
  updateEncounterRef.current = updateEncounter;
  queryClientRef.current = queryClient;

  const trackSave = (promise: Promise<void>) => {
    setPendingSaveCount((count) => count + 1);
    void promise.finally(() => {
      if (!mountedRef.current) return;
      setPendingSaveCount((count) => Math.max(0, count - 1));
    });
  };

  if (saveQueueRef.current == null || saveQueueScopeRef.current !== saveScope) {
    saveQueueScopeRef.current = saveScope;
    saveQueueRef.current = createScratchpadSaveQueue({
      save: (id, notes) =>
        updateEncounterRef.current({
          id,
          data: { scratchpadNotes: notes },
        }),
      onSuccess: (id, savedNotes, data) => {
        queryClientRef.current.setQueryData(
          getGetEncounterByIdQueryKey(id),
          (previous: unknown) => {
            const prior = previous as { data?: unknown } | undefined;
            return {
              ...(prior ?? {}),
              data,
            };
          },
        );
        if (
          boundEncounterIdRef.current === id &&
          notesRef.current === savedNotes
        ) {
          dirtyRef.current = false;
        }
      },
      onError: (id, _notes, error, bestEffort) => {
        if (boundEncounterIdRef.current === id) {
          dirtyRef.current = true;
        }
        captureException(error, {
          tags: { feature: "agenda-v2", action: "scratchpad-save" },
          extra: { encounterId: id },
        });
        if (bestEffort) return;
        // A failed save re-arms the 400ms autosave timer below, so a sustained
        // backend outage would otherwise stack a fresh toast every
        // AUTOSAVE_MS + round-trip. sonner only collapses toasts that share an
        // explicit id, so pin one: the physician sees a single persistent
        // "couldn't save" toast that updates, not a growing tower of them.
        toast.error(
          getServerErrorMessage(error) ??
            "Unable to save scratch pad. Please try again.",
          { id: SCRATCHPAD_SAVE_ERROR_TOAST_ID },
        );
        if (boundEncounterIdRef.current === id && !disabledRef.current) {
          scheduleSaveRef.current();
        }
      },
      scope: saveScope,
      state: scratchpadSaveState,
    });
  }

  clearSaveTimerRef.current = () => {
    if (saveTimerRef.current != null) {
      window.clearTimeout(saveTimerRef.current);
      saveTimerRef.current = null;
    }
  };

  enqueueSaveRef.current = (id, notes, bestEffort = false) => {
    const queue = saveQueueRef.current;
    if (!queue) return;
    trackSave(queue.enqueue(id, notes, bestEffort));
  };

  flushBoundEncounterRef.current = (options) => {
    const id = boundEncounterIdRef.current;
    if (id == null || !dirtyRef.current) return;
    enqueueSaveRef.current(id, notesRef.current, options?.bestEffort ?? false);
  };

  scheduleSaveRef.current = () => {
    if (disabledRef.current) return;
    const id = boundEncounterIdRef.current;
    if (id == null || !dirtyRef.current) return;
    clearSaveTimerRef.current();
    saveTimerRef.current = window.setTimeout(() => {
      saveTimerRef.current = null;
      flushBoundEncounterRef.current();
    }, AUTOSAVE_MS);
  };

  const serverNotes = encounterQuery.data?.data?.scratchpadNotes;
  const isEditorDisabled =
    disabled || encounterQuery.isLoading || encounterQuery.isError;

  const editor = useEditor({
    extensions: EDITOR_EXTENSIONS,
    content: EMPTY_DOC,
    immediatelyRender: false,
    editable: !isEditorDisabled,
    onUpdate({ editor: updatedEditor }) {
      notesRef.current = serializeScratchpadDoc(updatedEditor.getJSON());
      dirtyRef.current = true;
      const nextEmpty = updatedEditor.isEmpty;
      setIsEmpty((previous) => (previous === nextEmpty ? previous : nextEmpty));
      scheduleSaveRef.current();
    },
    editorProps: EDITOR_PROPS,
  });

  useLayoutEffect(() => {
    scratchpadSaveState.activateScope(saveScope);
  }, [saveScope]);

  useLayoutEffect(() => {
    const scopeChanged = boundSaveScopeRef.current !== saveScope;
    if (!scopeChanged && boundEncounterIdRef.current === encounterId) return;

    const previousId = boundEncounterIdRef.current;
    clearSaveTimerRef.current();

    if (!scopeChanged && previousId != null && dirtyRef.current) {
      enqueueSaveRef.current(previousId, notesRef.current, true);
    }

    boundEncounterIdRef.current = encounterId;
    boundSaveScopeRef.current = saveScope;
    dirtyRef.current = false;
    notesRef.current = "";

    if (editor) {
      editor.commands.setContent(EMPTY_DOC, { emitUpdate: false });
      setIsEmpty(true);
    }
  }, [editor, encounterId, saveScope]);

  useEffect(() => {
    if (!editor) return;
    editor.setEditable(!isEditorDisabled, false);
  }, [editor, isEditorDisabled]);

  useEffect(() => {
    if (!editor) return;
    if (!encounterQuery.isSuccess) return;
    if (boundEncounterIdRef.current !== encounterId) return;
    if (boundSaveScopeRef.current !== saveScope) return;
    if (dirtyRef.current) return;

    if (encounterId != null) {
      const pendingSave = scratchpadSaveState.getPending(
        saveScope,
        encounterId,
      );
      if (pendingSave !== undefined) {
        const pendingContent = parseScratchpadToEditorContent(
          pendingSave.notes,
        );
        notesRef.current = pendingSave.notes;
        dirtyRef.current = true;
        editor.commands.setContent(pendingContent, { emitUpdate: false });
        setIsEmpty(editor.isEmpty);
        void pendingSave.settled.then(() => {
          if (!mountedRef.current) return;
          if (boundEncounterIdRef.current !== encounterId) return;
          if (boundSaveScopeRef.current !== saveScope) return;
          const retry = saveQueueRef.current?.retryPending(
            encounterId,
            pendingSave,
          );
          if (retry !== undefined) {
            trackSave(retry);
          } else if (notesRef.current === pendingSave.notes) {
            dirtyRef.current = false;
          }
        });
        return;
      }
    }

    const nextContent = parseScratchpadToEditorContent(serverNotes ?? "");
    const nextSerialized = serializeScratchpadDoc(nextContent);
    if (serializeScratchpadDoc(editor.getJSON()) === nextSerialized) {
      setIsEmpty(editor.isEmpty);
      notesRef.current = nextSerialized;
      return;
    }
    editor.commands.setContent(nextContent, { emitUpdate: false });
    notesRef.current = nextSerialized;
    setIsEmpty(editor.isEmpty);
  }, [editor, encounterId, encounterQuery.isSuccess, saveScope, serverNotes]);

  useEffect(() => {
    if (!disabled) return;
    clearSaveTimerRef.current();
    // A disabled transition is a lifecycle boundary like scope-change and
    // unmount: retain the notes in the pending queue so they can be
    // recovered, since a failed save here cannot retry while disabled
    // (`scheduleSaveRef` no-ops when `disabledRef.current` is true).
    flushBoundEncounterRef.current({ bestEffort: true });
  }, [disabled]);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearSaveTimerRef.current();
      const id = boundEncounterIdRef.current;
      if (id == null || !dirtyRef.current) return;
      enqueueSaveRef.current(id, notesRef.current, true);
    };
  }, []);

  const status: AgendaScratchpadStatus = encounterQuery.isError
    ? "error"
    : pendingSaveCount > 0
      ? "saving"
      : "idle";

  return {
    canLoad,
    editor,
    isEmpty,
    isError: encounterQuery.isError,
    isFetching: encounterQuery.isFetching,
    isSuccess: encounterQuery.isSuccess,
    status,
    onRetry: () => void encounterQuery.refetch(),
  };
}

export { PLACEHOLDER_TEXT as AGENDA_SCRATCHPAD_PLACEHOLDER };
